<template>
  <div>
    <div class="row success">
      <div class="col-xl-8">
        <h5 style="margin-bottom: 30px">
          <span style="margin-right: 30px">{{
            $t("appreciation.teacherAppreciation")
          }}</span>
        </h5>
      </div>
    </div>
    <div class="row">
      <div class="col-xl-12">
        <label>{{ $t("bulletin.listeClasses") }}</label>
        <a-select
          show-search
          :placeholder="$t('emploi.listeClasses')"
          option-filter-prop="children"
          :filter-option="filterOption"
          style="width: 150px; margin-left: 10px"
          @change="
            (val) => {
              handleClassChange('classe', val);
            }
          "
        >
          <a-select-option value="all">---</a-select-option>
          <a-select-option
            v-for="classe in classes"
            :key="classe._id"
            :value="classe._id"
            >{{ classe.name }}</a-select-option
          >
        </a-select>

        <label style="margin-right: 5px; margin-left: 15px">{{
          $t("bulletin.Module")
        }}</label>
        <a-select
          show-search
          :placeholder="$t('bulletin.Module')"
          option-filter-prop="children"
          :filter-option="filterOption"
          style="width: 150px; margin-left: 10px"
          @change="handleModuleChange"
          :disabled="!selectedClasse"
          v-model="moduleType"
        >
          <a-select-option
            v-for="module in listModules"
            :key="module._id"
            :value="module._id"
          >
            {{ module.name }}
          </a-select-option>
        </a-select>

        <label style="margin-right: 5px; margin-left: 15px">{{
          $t("emploi.trimestre")
        }}</label>
        <a-select
          :placeholder="$t('paiement.selectionnerMois')"
          style="width: 100px; margin-left: 15px; margin-right: 20px"
          @change="handleTrimesterChange"
          default-value="1"
        >
          <a-select-option
            v-for="trimester in ['1', '2', '3']"
            :key="trimester"
            :value="trimester"
            >{{ trimester }}</a-select-option
          >
        </a-select>

        <a-button
          type="primary"
          style="margin-left: 10px"
          :disabled="!selectedClasse || selectedClasse === 'all'"
          @click="openTransferModal"
        >
          {{ $t("appreciation.transferAppreciation") }}
        </a-button>
      </div>
    </div>

    <a-modal
      :title="$t('appreciation.transferAppreciation')"
      :visible="transferModalVisible"
      :confirm-loading="transferLoading"
      :ok-text="$t('all.apply')"
      :cancel-text="$t('all.cancel')"
      width="820px"
      @ok="onTransferApply"
      @cancel="closeTransferModal"
    >
      <div class="transfer-meta">
        <span v-if="selectedClasseName">
          <b>{{ $t("bulletin.listeClasses") }}:</b>
          {{ selectedClasseName }}
        </span>
        <span v-if="selectedLevelLabel">
          <b>{{ $t("etablissement.niveau") }}:</b>
          {{ selectedLevelLabel }}
        </span>
        <span>
          <b>{{ $t("emploi.trimestre") }}:</b>
          {{ selectedTrimester }}
        </span>
      </div>

      <div class="transfer-layout">
        <div class="transfer-col">
          <div class="transfer-col-head">
            <span>{{ $t("appreciation.transferSource") }}</span>
            <span class="transfer-type-tag">{{ typeLabel(type) }}</span>
          </div>
          <a-radio-group
            v-model="transferSourceKey"
            class="transfer-tree"
            @change="onTransferSourceKeyChange"
          >
            <a-spin :spinning="transferSourceModulesLoading">
              <div
                v-for="mod in transferSourceModules"
                :key="mod._id"
                class="transfer-tree-item"
              >
                <a-radio :value="'m:' + mod._id">{{ mod.name }}</a-radio>
                <div
                  v-for="sm in getModuleSubmodules(mod)"
                  :key="sm._id"
                  class="transfer-tree-child"
                >
                  <a-radio :value="'s:' + mod._id + ':' + sm._id">
                    {{ sm.name }}
                  </a-radio>
                </div>
              </div>
              <div
                v-if="
                  !transferSourceModulesLoading && !transferSourceModules.length
                "
                class="transfer-empty"
              >
                {{ $t("appreciation.transferNoModules") }}
              </div>
            </a-spin>
          </a-radio-group>
        </div>

        <div class="transfer-divider"></div>

        <div class="transfer-col">
          <div class="transfer-col-head">
            <span>{{ $t("appreciation.transferTarget") }}</span>
          </div>
          <a-select
            v-model="transferTargetType"
            style="width: 100%; margin-bottom: 12px"
            :placeholder="$t('appreciation.selectTargetType')"
            @change="onTransferTargetTypeChange"
          >
            <a-select-option
              v-for="t in transferTargetTypeOptions"
              :key="t"
              :value="t"
            >
              {{ typeLabel(t) }}
            </a-select-option>
          </a-select>

          <a-spin :spinning="transferTargetModulesLoading">
            <div v-if="!transferTargetType" class="transfer-empty">
              {{ $t("appreciation.transferPickTypeFirst") }}
            </div>
            <div v-else class="transfer-tree">
              <div
                v-for="mod in transferTargetModules"
                :key="mod._id"
                class="transfer-tree-item"
              >
                <a-checkbox
                  :checked="isTargetModuleSelected(mod._id)"
                  :indeterminate="isTargetModuleIndeterminate(mod._id)"
                  @change="
                    (e) => onTargetModuleCheck(mod._id, e.target.checked)
                  "
                >
                  {{ mod.name }}
                </a-checkbox>
                <div
                  v-for="sm in getModuleSubmodules(mod)"
                  :key="sm._id"
                  class="transfer-tree-child"
                >
                  <a-checkbox
                    :checked="isTargetSubmoduleSelected(sm._id)"
                    @change="
                      (e) =>
                        onTargetSubmoduleCheck(
                          mod._id,
                          sm._id,
                          e.target.checked
                        )
                    "
                  >
                    {{ sm.name }}
                  </a-checkbox>
                </div>
              </div>

              <div
                v-if="!transferTargetModules.length"
                class="transfer-empty"
              >
                {{ $t("appreciation.transferNoModules") }}
              </div>

              <div class="transfer-selected-count">
                {{
                  $t("appreciation.targetModulesSelected", {
                    count: transferSelectedModuleCount,
                  })
                }}
              </div>
            </div>
          </a-spin>
        </div>
      </div>

      <a-alert
        type="info"
        show-icon
        class="transfer-policy"
        :message="$t('appreciation.transferOverwritePolicy')"
      >
        <template slot="description">
          <a-radio-group v-model="transferOverwritePolicy">
            <a-radio value="skip">
              {{ $t("appreciation.transferSkipExisting") }}
            </a-radio>
            <a-radio value="replace">
              {{ $t("appreciation.transferReplaceExisting") }}
            </a-radio>
          </a-radio-group>
        </template>
      </a-alert>
    </a-modal>

    <a-modal
      :title="$t('appreciation.transferConfirmTitle')"
      :visible="transferConfirmVisible"
      :confirm-loading="transferLoading"
      :ok-text="$t('action.confirmer')"
      :cancel-text="$t('all.cancel')"
      width="520px"
      @ok="confirmTransfer"
      @cancel="transferConfirmVisible = false"
    >
      <div class="transfer-confirm">
        <div class="transfer-confirm-row">
          <span class="transfer-confirm-label">{{ $t("all.source") }}</span>
          <span class="transfer-confirm-value">
            {{ transferConfirmSummary.sourceLabel }}
            <span class="transfer-type-tag">{{
              transferConfirmSummary.sourceType
            }}</span>
          </span>
        </div>
        <div class="transfer-confirm-row">
          <span class="transfer-confirm-label">{{ $t("all.targets") }}</span>
          <span class="transfer-confirm-value">
            <span class="transfer-type-tag">{{
              transferConfirmSummary.targetType
            }}</span>
          </span>
        </div>
        <ul class="transfer-confirm-list">
          <li
            v-for="(name, idx) in transferConfirmSummary.targetLabels"
            :key="idx"
          >
            {{ name }}
          </li>
        </ul>
        <a-alert
          type="info"
          show-icon
          :message="transferConfirmSummary.policyLabel"
        />
      </div>
    </a-modal>

    <span
      v-if="studentsWithoutAppreciationCount !== null"
      style="margin-top: 20px"
    >
      <!-- Overall Status Banner -->
      <a-alert
        :type="studentsWithoutAppreciationCount > 0 ? 'warning' : 'success'"
        :message="
          studentsWithoutAppreciationCount +
          ' ' +
          (studentsWithoutAppreciationCount > 0
            ? $t('appreciation.studentsWithoutAppreciation')
            : $t('appreciation.allStudentsHaveAppreciations'))
        "
        class="mt-2 mb-4"
        show-icon
      />
      <div v-if="teacherByModule.length > 0" style="width: 100%">
        <div
          v-for="teacher in teacherByModule"
          :key="teacher.teacherId"
          style="
            margin-bottom: 10px;
            width: 100%;
            border-left: 2px solid #1890ff;
            padding-left: 16px;
          "
        >
          <div style="font-size: 16px; font-weight: bold">
            <span>{{ teacher.teacherName }}</span>
          </div>

          <!-- SubModules Tree Nodes -->
          <div v-if="teacher.SubModules" style="padding-left: 24px">
            <div
              v-for="subModule in teacher.SubModules"
              :key="subModule._id"
              style="display: flex; align-items: center; margin-bottom: 6px"
            >
              <div style="display: flex; align-items: center; width: 100%">
                <span
                  style="display: flex; align-items: center; margin-right: 10px"
                >
                  <span style="margin-right: 8px">└→</span>
                  <b>{{ $t("bulletin.SubModule") }}: </b>
                  <span style="margin-left: 4px">{{ subModule.name }}</span>
                </span>

                <span style="display: flex; align-items: center; gap: 4px">
                  <span style="font-size: 17px; color: red">
                    {{
                      totalStudent < subModule.Appreciations
                        ? 0
                        : totalStudent - subModule.Appreciations
                    }}
                  </span>
                  <small class="text-muted">{{
                    $t("appreciation.missingAppreciation")
                  }}</small>

                  <span style="font-size: 17px; color: #52c41a">
                    {{ subModule.Appreciations }}
                  </span>
                  <small class="text-muted">{{
                    $t("appreciation.appreciationGiven")
                  }}</small>
                </span>
              </div>
            </div>
          </div>

          <!-- Module Tree Node -->
          <div v-if="!teacher.SubModules" style="padding-left: 24px">
            <div style="display: flex; align-items: center; margin-bottom: 8px">
              <span
                style="display: flex; align-items: center; min-width: 200px"
              >
                <span style="margin-right: 8px">└→</span>
                <b>{{ $t("bulletin.Module") }}: </b>
                <span style="margin-left: 4px">{{ teacher.module.name }}</span>
              </span>

              <span style="display: flex; align-items: center; gap: 10px">
                <span
                  style="font-size: 18px; font-weight: bold; color: #ff4d4f"
                >
                  {{
                    totalStudent < teacher.module.Appreciations
                      ? "0"
                      : totalStudent - teacher.module.Appreciations
                  }}
                </span>
                <span style="color: #666; font-size: 14px">{{
                  $t("appreciation.missingAppreciation")
                }}</span>

                <span
                  style="
                    margin-left: 15px;
                    font-size: 18px;
                    font-weight: bold;
                    color: #52c41a;
                  "
                >
                  {{ teacher.module.Appreciations }}
                </span>
                <span style="color: #666; font-size: 14px">{{
                  $t("appreciation.appreciationGiven")
                }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </span>
    <div class="row mt-4">
      <div class="col-xl-12">
        <div class="card" v-if="selectedClasse">
          <div class="data-table">
            <a-table
              @change="tableChanged"
              :loading="tableLoading"
              :pagination="true"
              :data-source="activeData"
              :columns="columns"
            >
              <template slot="name" slot-scope="text">
                <span class="arabic-font">{{ text }}</span>
              </template>

              <template slot="teacherName" slot-scope="text">
                <div style="white-space: nowrap">
                  {{ text }}
                </div>
              </template>

              <template slot="appreciation" slot-scope="text">
                <a-tooltip :title="text">
                  <div
                    style="
                      max-width: 400px;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                    "
                  >
                    {{
                      text.length > 100 ? text.substring(0, 100) + "..." : text
                    }}
                  </div>
                </a-tooltip>
              </template>

              <template slot="module" slot-scope="text">
                <div style="white-space: nowrap">
                  {{ text }}
                </div>
              </template>
              <template slot="updatedAt" slot-scope="text">
                {{ text }}
              </template>
              <div
                slot="filterDropdown"
                slot-scope="{
                  setSelectedKeys,
                  selectedKeys,
                  confirm,
                  clearFilters,
                  column,
                }"
                style="padding: 8px"
              >
                <a-input
                  v-ant-ref="(c) => (searchInput = c)"
                  :placeholder="`${$t('personnel.chercher')} ${column.title}`"
                  :value="selectedKeys[0]"
                  style="width: 188px; margin-bottom: 8px; display: block"
                  @change="
                    (e) =>
                      setSelectedKeys(e.target.value ? [e.target.value] : [])
                  "
                  @pressEnter="
                    () => handleSearch(selectedKeys, confirm, column.dataIndex)
                  "
                />
                <a-button
                  type="primary"
                  icon="search"
                  size="small"
                  style="width: 90px; margin-right: 8px"
                  @click="
                    () => handleSearch(selectedKeys, confirm, column.dataIndex)
                  "
                  >{{ $t("paiement.chercher") }}</a-button
                >
                <a-button
                  size="small"
                  style="width: 90px"
                  @click="() => handleReset(clearFilters)"
                  >{{ $t("action.reinitialiser") }}</a-button
                >
              </div>

              <a-icon
                slot="filterIcon"
                slot-scope="filtered"
                type="search"
                :style="{ color: filtered ? '#108ee9' : undefined }"
              />
            </a-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/*eslint-disable */
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import moment from "moment";
import { getAllLevels } from "@/config/LevelConstants";

const TYPE_LABEL_KEYS = {
  MP: "moduleType.pedagogicalModule",
  MT: "moduleType.pilotModule",
  MS: "moduleType.specificModule",
};

export default {
  props: {
    type: {
      type: String,
      required: true,
    },
    subModuleList: {
      type: Array,
      default: () => [],
    },
  },
  async created() {
    this.tableLoading = true;

    await apiClient
      .post("/classrooms/filter", {
        query: {
          status: "active",
        },
        aggregation: {
          $project: {
            _id: 1,
            name: 1,
            level: 1,
            bulletin: 1,
          },
        },
      })
      .then((res) => (this.classes = res.data))
      .catch((e) => {
        console.log(e);
      });

    this.filtredTable = this.activeData;
    this.tableLoading = false;
  },
  data() {
    return {
      selectedTrimester: "1",
      selectedClasse: null,
      moduleType: null,
      tableLoading: false,
      activeData: [],
      filtredTable: [],
      classes: [],
      listModules: [],
      teacherByModule: [],
      totalStudent: 0,
      searchText: "",
      searchInput: null,
      searchedColumn: "",
      studentsWithoutAppreciationCount: null,
      level: null,
      selectedClasseName: null,
      transferModalVisible: false,
      transferLoading: false,
      transferSourceKey: null,
      transferSourceModuleId: null,
      transferSourceSubmoduleId: null,
      transferTargetType: null,
      transferSourceModules: [],
      transferSourceModulesLoading: false,
      transferTargetModules: [],
      transferTargetModulesLoading: false,
      transferTargetModuleIds: [],
      transferTargetSubmoduleIds: [],
      transferOverwritePolicy: "skip",
      transferConfirmVisible: false,
      transferPendingTargets: [],
      transferConfirmSummary: {
        sourceLabel: "",
        sourceType: "",
        targetType: "",
        targetLabels: [],
        policyLabel: "",
      },
    };
  },
  computed: {
    ...mapState(["settings", "user"]),
    transferTargetTypeOptions() {
      return ["MP", "MS", "MT"].filter((t) => t !== this.type);
    },
    schoolDetails() {
      return (
        (this.user.building || []).find(
          (el) => el.dbName == this.settings.activeBuilding
        ) || {}
      );
    },
    selectedLevelLabel() {
      if (this.level === null || this.level === undefined) return "";
      const levels = getAllLevels(
        this.$t.bind(this),
        this.schoolDetails.countryCode || "TN"
      );
      return levels[String(this.level)] || String(this.level);
    },
    transferSelectedModuleCount() {
      return this.transferTargetModules.filter((mod) =>
        this.isTargetModuleSelected(mod._id)
      ).length;
    },
    columns() {
      const baseColumns = [
        {
          title: this.$t("bulletin.nom"),
          dataIndex: "fullName",
          key: "fullName",
          scopedSlots: {
            customRender: "name",
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
          },
          onFilter: (value, record) =>
            record.fullName
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              }, 0);
            }
          },
        },
      ];
      if (!this.moduleType) {
        baseColumns.push({
          title: this.$t("bulletin.Module"),
          dataIndex: "module",
          key: "module",
          scopedSlots: {
            customRender: "module",
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
          },
          onFilter: (value, record) =>
            record.module
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              }, 0);
            }
          },
        });
      }
      baseColumns.push(
        {
          title: this.$t("bulletin.SubModule"),
          dataIndex: "sousModule",
          key: "sousModule",
          scopedSlots: {
            customRender: "sousModule",
          },
        },
        {
          title: this.$t("emploi.enseignant"),
          dataIndex: "teacherName",
          key: "teacherName",
          scopedSlots: {
            customRender: "teacherName",
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
          },
          onFilter: (value, record) =>
            record.teacherName
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              }, 0);
            }
          },
        },
        {
          title: this.$t("appreciation.appreciation"),
          dataIndex: "appreciation",
          key: "appreciation",
          scopedSlots: {
            customRender: "appreciation",
          },
        },
        {
          title: this.$t("profil.lastModifi") || "Last Update",
          dataIndex: "updatedAt",
          key: "updatedAt",
          scopedSlots: {
            customRender: "updatedAt",
          },
        }
      );

      return baseColumns;
    },
  },
  methods: {
    async handleTrimesterChange(value) {
      this.selectedTrimester = value;
      this.moduleType = null;
      if (this.level) {
        await this.getModules(this.level);
      }
      await this.updateTeacherAppreciations();
    },

    tableChanged(pagination, filters, sorter, extra) {
      this.filtredTable = extra.currentDataSource;
    },

    async handleClassChange(on, value) {
      this.selectedClasse = value;
      this.moduleType = null;
      this.totalStudent = 0;
      this.teacherByModule = [];
      this.studentsWithoutAppreciationCount = null;
      this.activeData = [];
      this.selectedClasseName = null;

      if (!value || value === "all") {
        this.level = null;
        this.listModules = [];
        return;
      }

      const classe = this.classes.find((c) => c._id == value);
      if (!classe) return;

      this.tableLoading = true;
      this.level = classe.level;
      this.selectedClasseName = classe.name;
      try {
        await this.getModules(classe.level);
        await this.updateTeacherAppreciations();
      } finally {
        this.tableLoading = false;
      }
    },

    async handleModuleChange(value) {
      this.moduleType = value;
      await this.updateTeacherAppreciations();
    },
    async getModules(level) {
      await apiClient
        .post("/bulletin/v2/module/filter/marks", {
          status: "active",
          level: level,
          trimester: this.selectedTrimester,
          type: this.type,
        })
        .then((res) => {
          this.subjectsLoading = true;
          this.listModules = [];
          if (res.data.length == 0)
            return this.$message.warning(this.$t("error.aucModule"));
          res.data.forEach((moduleItem) => {
            moduleItem.subjects = moduleItem.subjects.map((subject) => {
              const subjectsInfo = moduleItem.subjectsInfo;
              const subsubjects = subjectsInfo[subject._id];
              subject.moduleName = moduleItem.name;
              if (subsubjects && subsubjects.sousModule)
                subject.subModule = (
                  Array.isArray(this.subModuleList) ? this.subModuleList : []
                ).find(
                  (sm) => String(sm._id) == String(subsubjects.sousModule)
                );
              return subject;
            });
            this.listModules.push(moduleItem);
          });
          this.subjectsLoading = false;
        })
        .catch((e) => {
          this.$message.error(this.$t("error.erreur"));
          this.moduleLoading = false;
        });
    },
    async updateTeacherAppreciations() {
      if (!this.selectedClasse || this.selectedClasse === "all") return;

      this.tableLoading = true;
      try {
        const query = {
          classroom: this.selectedClasse,
          trimester: this.selectedTrimester,
          schoolarYear: this.settings.activeSchoolarYear,
          type: this.type,
        };
        if (this.moduleType) {
          query.module = this.moduleType;
        }
        const { data } = await apiClient.post(
          "/bulletin/v2/appreciation/filter/manager",
          {
            modelName: "AppreciationGeneral",
            query: query,
          }
        );
        this.totalStudent = data.totalStudent || 0;
        let teachersOfThatModule = [];
        if (this.moduleType) {
          (data.access || []).forEach((item) => {
            if (item.appAccess) {
              item.appAccess.forEach((accessItem) => {
                if (
                  accessItem.module &&
                  accessItem.module._id == this.moduleType
                ) {
                  teachersOfThatModule.push({
                    teacherName:
                      item.teacher && item.teacher.employee
                        ? `${item.teacher.employee.firstName} ${item.teacher.employee.lastName}`
                        : "Unknown Teacher",
                    teacherId: item.teacher ? item.teacher._id : null,
                    SubModules: accessItem.haveSubMod
                      ? (accessItem.sousModules || []).map((sm) => ({
                          _id: sm._id,
                          name: sm.name,
                          Appreciations: 0,
                        }))
                      : null,
                    module: {
                      _id: accessItem.module._id,
                      name: accessItem.module.name,
                      Appreciations: 0,
                    },
                  });
                }
              });
            }
          });
        }

        const hasAppreciation = (appreciation) =>
          !!appreciation && appreciation !== "--";

        if (data.allAppreciations) {
          this.activeData = data.allAppreciations.map((item) => ({
            key:
              item._id ||
              `${item.student?._id || "s"}-${item.module?._id || "m"}-${item.sousModule?._id || "sm"}`,
            fullName: item.student
              ? `  ${item.student.firstName}${item.student.middleName ? " " + item.student.middleName : ""} ${item.student.lastName}`
              : "--",
            module: item.module?.name || "--",
            sousModule: item.sousModule?.name || "--",
            teacherName: item.teacher
              ? item.teacher.employee &&
                `${item.teacher.employee.firstName} ${item.teacher.employee.lastName}`
              : "-    -",
            appreciation: item.appreciation || "--",
            updatedAt: item.updatedAt
              ? moment(item.updatedAt).format("DD/MM/YYYY")
              : "--",
          }));
          teachersOfThatModule.forEach((teacherModule) => {
            data.allAppreciations.forEach((item) => {
              if (item.teacher && item.teacher._id == teacherModule.teacherId) {
                if (teacherModule.SubModules && item.sousModule) {
                  teacherModule.SubModules.forEach((sm) => {
                    if (
                      String(sm._id) === String(item.sousModule._id) &&
                      hasAppreciation(item.appreciation)
                    ) {
                      sm.Appreciations++;
                    }
                  });
                }
                if (hasAppreciation(item.appreciation)) {
                  teacherModule.module.Appreciations++;
                }
              }
            });
          });
        }
        const studentsWithoutAppreciation = new Set();
        (data.allAppreciations || []).forEach((item) => {
          if (!hasAppreciation(item.appreciation) && item.student?._id) {
            studentsWithoutAppreciation.add(String(item.student._id));
          }
        });
        this.studentsWithoutAppreciationCount =
          studentsWithoutAppreciation.size;
        this.teacherByModule = teachersOfThatModule;
      } catch (error) {
        console.error(error);
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.tableLoading = false;
      }
    },

    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },
    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm();
      this.searchText = selectedKeys[0];
      this.searchedColumn = dataIndex;
    },

    handleReset(clearFilters) {
      clearFilters();
      this.searchText = "";
    },

    typeLabel(type) {
      const key = TYPE_LABEL_KEYS[type];
      return key ? this.$t(key) : type;
    },

    getModuleSubmodules(mod) {
      if (!mod || !mod.subjectsInfo) return [];
      const list = Array.isArray(this.subModuleList) ? this.subModuleList : [];
      const seen = new Set();
      const result = [];
      Object.values(mod.subjectsInfo).forEach((info) => {
        if (!info || !info.sousModule) return;
        const id = String(info.sousModule);
        if (seen.has(id)) return;
        seen.add(id);
        const sm = list.find((s) => String(s._id) === id);
        if (sm) result.push(sm);
      });
      return result;
    },

    matchByName(sourceName, items) {
      const name = (sourceName || "").trim().toLowerCase();
      if (!name) return [];
      return items.filter(
        (item) => (item.name || "").trim().toLowerCase() === name
      );
    },

    resetTransferForm() {
      this.transferSourceKey = null;
      this.transferSourceModuleId = null;
      this.transferSourceSubmoduleId = null;
      this.transferTargetType = null;
      this.transferSourceModules = [];
      this.transferTargetModules = [];
      this.transferTargetModuleIds = [];
      this.transferTargetSubmoduleIds = [];
      this.transferOverwritePolicy = "skip";
      this.transferConfirmVisible = false;
      this.transferPendingTargets = [];
    },

    async fetchTransferModules(type) {
      const { data } = await apiClient.post("/bulletin/v2/module/filter", {
        query: {
          status: "active",
          level: this.level,
          trimester: Number(this.selectedTrimester),
          type,
          schoolarYear: this.settings.activeSchoolarYear,
        },
      });
      return data || [];
    },

    async openTransferModal() {
      if (!this.selectedClasse || this.selectedClasse === "all" || !this.level) {
        return;
      }
      this.resetTransferForm();
      this.transferModalVisible = true;
      this.transferSourceModulesLoading = true;
      try {
        this.transferSourceModules = await this.fetchTransferModules(this.type);
      } catch (e) {
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.transferSourceModulesLoading = false;
      }
    },

    closeTransferModal() {
      this.transferModalVisible = false;
      this.transferLoading = false;
      this.resetTransferForm();
    },

    onTransferSourceKeyChange() {
      const key = this.transferSourceKey;
      if (!key) {
        this.transferSourceModuleId = null;
        this.transferSourceSubmoduleId = null;
        return;
      }
      if (String(key).startsWith("s:")) {
        const parts = String(key).slice(2).split(":");
        this.transferSourceModuleId = parts[0];
        this.transferSourceSubmoduleId = parts[1] || null;
      } else if (String(key).startsWith("m:")) {
        this.transferSourceModuleId = String(key).slice(2);
        this.transferSourceSubmoduleId = null;
      }
      this.autoMatchTargetModules();
      this.autoMatchTargetSubmodules();
    },

    async onTransferTargetTypeChange(type) {
      this.transferTargetModuleIds = [];
      this.transferTargetSubmoduleIds = [];
      this.transferTargetModules = [];
      if (!type || !this.level) return;

      this.transferTargetModulesLoading = true;
      try {
        this.transferTargetModules = await this.fetchTransferModules(type);
        this.autoMatchTargetModules();
        this.autoMatchTargetSubmodules();
      } catch (e) {
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.transferTargetModulesLoading = false;
      }
    },

    isTargetModuleSelected(moduleId) {
      const mod = this.transferTargetModules.find(
        (m) => String(m._id) === String(moduleId)
      );
      const children = this.getModuleSubmodules(mod);
      if (!children.length) {
        return this.transferTargetModuleIds.some(
          (id) => String(id) === String(moduleId)
        );
      }
      return children.every((sm) => this.isTargetSubmoduleSelected(sm._id));
    },

    isTargetModuleIndeterminate(moduleId) {
      const mod = this.transferTargetModules.find(
        (m) => String(m._id) === String(moduleId)
      );
      const children = this.getModuleSubmodules(mod);
      if (!children.length) return false;
      const selectedCount = children.filter((sm) =>
        this.isTargetSubmoduleSelected(sm._id)
      ).length;
      return selectedCount > 0 && selectedCount < children.length;
    },

    isTargetSubmoduleSelected(submoduleId) {
      return this.transferTargetSubmoduleIds.some(
        (id) => String(id) === String(submoduleId)
      );
    },

    syncTargetModuleId(moduleId, children) {
      const allSelected =
        children.length > 0 &&
        children.every((sm) => this.isTargetSubmoduleSelected(sm._id));
      const hasModule = this.transferTargetModuleIds.some(
        (id) => String(id) === String(moduleId)
      );
      if (allSelected && !hasModule) {
        this.transferTargetModuleIds = [
          ...this.transferTargetModuleIds,
          moduleId,
        ];
      } else if (!allSelected && hasModule) {
        this.transferTargetModuleIds = this.transferTargetModuleIds.filter(
          (id) => String(id) !== String(moduleId)
        );
      }
    },

    onTargetModuleCheck(moduleId, checked) {
      const mod = this.transferTargetModules.find(
        (m) => String(m._id) === String(moduleId)
      );
      const children = this.getModuleSubmodules(mod);

      if (checked) {
        if (
          !this.transferTargetModuleIds.some(
            (id) => String(id) === String(moduleId)
          )
        ) {
          this.transferTargetModuleIds = [
            ...this.transferTargetModuleIds,
            moduleId,
          ];
        }
        if (children.length) {
          const existing = new Set(
            this.transferTargetSubmoduleIds.map((id) => String(id))
          );
          children.forEach((sm) => existing.add(String(sm._id)));
          this.transferTargetSubmoduleIds = Array.from(existing);
        }
        return;
      }

      this.transferTargetModuleIds = this.transferTargetModuleIds.filter(
        (id) => String(id) !== String(moduleId)
      );
      if (children.length) {
        const childIds = new Set(children.map((sm) => String(sm._id)));
        this.transferTargetSubmoduleIds = this.transferTargetSubmoduleIds.filter(
          (id) => !childIds.has(String(id))
        );
      }
    },

    onTargetSubmoduleCheck(moduleId, submoduleId, checked) {
      const mod = this.transferTargetModules.find(
        (m) => String(m._id) === String(moduleId)
      );
      const children = this.getModuleSubmodules(mod);

      if (checked) {
        if (!this.isTargetSubmoduleSelected(submoduleId)) {
          this.transferTargetSubmoduleIds = [
            ...this.transferTargetSubmoduleIds,
            submoduleId,
          ];
        }
      } else {
        this.transferTargetSubmoduleIds = this.transferTargetSubmoduleIds.filter(
          (id) => String(id) !== String(submoduleId)
        );
      }

      // Parent module is selected only when every submodule is selected
      this.syncTargetModuleId(moduleId, children);
    },

    autoMatchTargetModules() {
      this.transferTargetModuleIds = [];
      this.transferTargetSubmoduleIds = [];
      if (!this.transferSourceModuleId || !this.transferTargetModules.length) {
        return;
      }

      // Source is a single submodule → only match that submodule by name
      if (this.transferSourceSubmoduleId) {
        const sourceMod = this.transferSourceModules.find(
          (m) => String(m._id) === String(this.transferSourceModuleId)
        );
        const sourceSm = this.getModuleSubmodules(sourceMod).find(
          (sm) => String(sm._id) === String(this.transferSourceSubmoduleId)
        );
        if (!sourceSm) return;

        this.transferTargetModules.forEach((mod) => {
          this.matchByName(
            sourceSm.name,
            this.getModuleSubmodules(mod)
          ).forEach((sm) => {
            this.onTargetSubmoduleCheck(mod._id, sm._id, true);
          });
        });
        return;
      }

      // Source is a module → match modules by name (selects all submodules if any)
      const source = this.transferSourceModules.find(
        (m) => String(m._id) === String(this.transferSourceModuleId)
      );
      if (!source) return;
      this.matchByName(source.name, this.transferTargetModules).forEach(
        (mod) => {
          this.onTargetModuleCheck(mod._id, true);
        }
      );
    },

    autoMatchTargetSubmodules() {
      // Handled inside autoMatchTargetModules
    },

    buildTransferTargets() {
      const targets = [];
      this.transferTargetModules.forEach((mod) => {
        const children = this.getModuleSubmodules(mod);
        if (!children.length) {
          if (this.isTargetModuleSelected(mod._id)) {
            targets.push({ module: mod._id, sousModule: null });
          }
          return;
        }
        children.forEach((sm) => {
          if (this.isTargetSubmoduleSelected(sm._id)) {
            targets.push({ module: mod._id, sousModule: sm._id });
          }
        });
      });
      return targets;
    },

    onTransferApply() {
      if (!this.transferSourceModuleId) {
        this.$message.warning(this.$t("appreciation.transferSelectSource"));
        return;
      }
      if (!this.transferTargetType) {
        this.$message.warning(this.$t("appreciation.selectTargetType"));
        return;
      }
      const targets = this.buildTransferTargets();
      if (!targets.length) {
        this.$message.warning(
          this.$t("appreciation.transferSelectTargetModules")
        );
        return;
      }

      const source = this.transferSourceModules.find(
        (m) => String(m._id) === String(this.transferSourceModuleId)
      );
      const sourceSm = this.getModuleSubmodules(source).find(
        (sm) => String(sm._id) === String(this.transferSourceSubmoduleId)
      );

      const targetLabels = [];
      this.transferTargetModules.forEach((mod) => {
        const children = this.getModuleSubmodules(mod);
        if (!children.length) {
          if (this.isTargetModuleSelected(mod._id)) {
            targetLabels.push(mod.name);
          }
          return;
        }
        const selectedChildren = children.filter((sm) =>
          this.isTargetSubmoduleSelected(sm._id)
        );
        if (!selectedChildren.length) return;
        if (selectedChildren.length === children.length) {
          targetLabels.push(mod.name);
        } else {
          selectedChildren.forEach((sm) => {
            targetLabels.push(`${mod.name} / ${sm.name}`);
          });
        }
      });

      this.transferPendingTargets = targets;
      this.transferConfirmSummary = {
        sourceLabel: source
          ? sourceSm
            ? `${source.name} / ${sourceSm.name}`
            : source.name
          : "--",
        sourceType: this.typeLabel(this.type),
        targetType: this.typeLabel(this.transferTargetType),
        targetLabels,
        policyLabel:
          this.transferOverwritePolicy === "replace"
            ? this.$t("appreciation.transferReplaceExisting")
            : this.$t("appreciation.transferSkipExisting"),
      };
      this.transferConfirmVisible = true;
    },

    confirmTransfer() {
      return this.submitTransfer(this.transferPendingTargets);
    },

    async submitTransfer(targets) {
      this.transferLoading = true;
      try {
        const { data } = await apiClient.post(
          "/bulletin/v2/appreciation/transfer",
          {
            classroom: this.selectedClasse,
            trimester: this.selectedTrimester,
            schoolarYear: this.settings.activeSchoolarYear,
            sourceType: this.type,
            sourceModule: this.transferSourceModuleId,
            sourceSousModule: this.transferSourceSubmoduleId || null,
            targetType: this.transferTargetType,
            targets: targets || this.buildTransferTargets(),
            overwritePolicy: this.transferOverwritePolicy,
          }
        );
        this.$message.success(
          this.$t("appreciation.transferResult", {
            copied: data.copied || 0,
            skipped: data.skipped || 0,
          })
        );
        this.transferConfirmVisible = false;
        this.closeTransferModal();
        await this.updateTeacherAppreciations();
      } catch (e) {
        this.$message.error(this.$t("error.erreur"));
      } finally {
        this.transferLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.arabic-font {
  font-size: 14px !important;
  font-family: Verdana, Geneva, Tahoma, sans-serif !important;
}

.success .ant-btn-danger {
  background: #8bc34a !important;
  border-color: #8bc34a !important;
}

.transfer-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  font-size: 13px;
  color: #595959;
}

.transfer-layout {
  display: flex;
  gap: 0;
  min-height: 280px;
}

.transfer-col {
  flex: 1;
  min-width: 0;
  padding: 12px;
  background: #fcfcfc;
  border-radius: 4px;
}

.transfer-col-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  font-weight: 600;
  font-size: 14px;
  color: #262626;
}

.transfer-type-tag {
  font-size: 12px;
  font-weight: 500;
  color: #1890ff;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  padding: 1px 8px;
}

.transfer-divider {
  width: 1px;
  background: #e8e8e8;
  margin: 0 10px;
  align-self: stretch;
}

.transfer-tree {
  display: block;
  width: 100%;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 4px;
}

.transfer-tree-item {
  margin-bottom: 8px;
  padding: 4px 6px;
  border-radius: 4px;
}

.transfer-tree-item:hover {
  background: #f5f5f5;
}

.transfer-tree-child {
  padding-left: 24px;
  margin-top: 4px;
}

.transfer-selected-count {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
  font-weight: 600;
  color: #262626;
}

.transfer-empty {
  color: #8c8c8c;
  font-size: 13px;
  padding: 8px 0;
}

.transfer-policy {
  margin-top: 14px;
}

.transfer-policy .ant-radio-wrapper {
  display: block;
  margin: 6px 0;
}

.transfer-confirm-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.transfer-confirm-label {
  color: #8c8c8c;
  font-size: 13px;
  min-width: 70px;
}

.transfer-confirm-value {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  text-align: right;
  font-weight: 500;
  color: #262626;
}

.transfer-confirm-list {
  margin: 0 0 14px;
  padding: 10px 12px 10px 28px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  max-height: 160px;
  overflow-y: auto;
}

.transfer-confirm-list li {
  margin: 4px 0;
  color: #262626;
}
</style>
